import { toId } from "../../utils/examsschedule/examHelpers";
import StaffByRoleSelect from "./StaffByRoleSelect";
import SeatingGrid from "./SeatingGrid";

/**
 * One room allocation for seating plan editor.
 * Room = ExamRoom master.
 * Invigilators = staff by role.
 * Students = bench × seat grid (seatAllocations).
 */
export default function SeatingRoomCard({
  allocation,
  index,
  roomOptions = [],
  studentOptions = [],
  classId = "",
  isPortal = false,
  onChange,
  onRemove,
  disabled = false,
}) {
  const roomId = toId(allocation.roomId);
  const invigilatorIds = (allocation.invigilatorIds || []).map(toId);
  const seatAllocations = allocation.seatAllocations || [];

  const selectedRoom = roomOptions.find((r) => toId(r) === roomId);
  const totalBenches =
    allocation.totalBenches || selectedRoom?.totalBenches || 0;
  const seatsPerBench =
    allocation.seatsPerBench || selectedRoom?.seatsPerBench || 1;
  const capacity = allocation.capacity || selectedRoom?.capacity || 0;

  const setField = (patch) => onChange?.(index, { ...allocation, ...patch });

  const handleRoomChange = (id) => {
    const room = roomOptions.find((r) => toId(r) === id);
    const nextSeats = room
      ? (seatAllocations || []).filter(
          (a) =>
            Number(a.benchNo) <= Number(room.totalBenches) &&
            Number(a.seatIndex || 1) <= Number(room.seatsPerBench || 1)
        )
      : [];
    setField({
      roomId: id,
      totalBenches: room?.totalBenches || 0,
      seatsPerBench: room?.seatsPerBench || 1,
      capacity: room?.capacity || 0,
      seatAllocations: nextSeats,
    });
  };

  return (
    <div className="border border-gray-200 dark:border-neutral-800 rounded-xl p-4 bg-white dark:bg-neutral-900 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-neutral-100">
          Room #{index + 1}
          {selectedRoom?.roomNo ? ` — ${selectedRoom.roomNo}` : ""}
        </h4>
        {!disabled && (
          <button
            type="button"
            onClick={() => onRemove?.(index)}
            className="text-xs text-red-600 hover:underline"
          >
            Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 dark:text-neutral-400 mb-1">Room *</label>
          <select
            disabled={disabled}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-900"
            value={roomId}
            onChange={(e) => handleRoomChange(e.target.value)}
          >
            <option value="">Select room</option>
            {roomOptions.map((r) => (
              <option key={toId(r)} value={toId(r)}>
                {r.roomNo}
                {r.name ? ` — ${r.name}` : ""} (cap {r.capacity}
                {r.seatsPerBench ? `, ${r.seatsPerBench}/bench` : ""},{" "}
                {r.totalBenches || "?"} benches)
              </option>
            ))}
          </select>
          {selectedRoom && (
            <p className="text-[11px] text-gray-400 dark:text-neutral-500 mt-1">
              {totalBenches} benches · {seatsPerBench}/bench · capacity{" "}
              {capacity}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 dark:text-neutral-400 mb-1">Start</label>
            <input
              type="time"
              disabled={disabled}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={allocation.startTime || ""}
              onChange={(e) => setField({ startTime: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-neutral-400 mb-1">End</label>
            <input
              type="time"
              disabled={disabled}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={allocation.endTime || ""}
              onChange={(e) => setField({ endTime: e.target.value })}
            />
          </div>
        </div>
      </div>

      <StaffByRoleSelect
        isPortal={isPortal}
        value={invigilatorIds}
        onChange={(ids) => setField({ invigilatorIds: ids })}
        disabled={disabled}
        preferredRoleNames={["Teacher", "Invigilator", "Staff"]}
        multi
        label="Invigilators (by role)"
      />

      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-2">
          Student seating grid
        </label>
        <SeatingGrid
          totalBenches={totalBenches}
          seatsPerBench={seatsPerBench}
          seatAllocations={seatAllocations}
          studentOptions={studentOptions}
          classId={classId}
          disabled={disabled || !roomId}
          onChange={(next) => setField({ seatAllocations: next })}
        />
      </div>
    </div>
  );
}
